/**
 * Instruction Library — admin curation worklist.
 *
 * Reviews reusable CRF completion instructions harvested from generated
 * guides. Backed by four admin-gated callables (listInstructionLibrary,
 * reviewInstruction, draftGeneralizedInstruction,
 * approveGeneralizedInstruction). No client-side admin gate — non-admins
 * get a permission error from the backend, same posture as AdminUsers.
 *
 * Single-page utility UI: loads ALL entries of the selected review status
 * up front (paged behind the scenes), then facets/sorts client-side. Has an
 * inline per-entry review card (approve for client / approve for everyone /
 * reject) and cluster review (draft + approve one generalized instruction
 * replacing a group of per-client variants of the same question).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from '../lib/callable';
import { Link } from 'react-router-dom';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { useToast } from '../contexts/ToastContext';
import {
  ArrowLeft,
  BookOpen,
  RefreshCw,
  AlertTriangle,
  Loader2,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// ── Wire types ─────────────────────────────────────────────────────────

interface LibraryEntry {
  id: string;
  entryId: string;
  client: string | null;
  variable: string | null;
  isStandardVariable: boolean;
  normalizedLabel: string;
  questionType: string;
  instruction: string;
  source: 'ai' | 'fallback';
  status: 'unreviewed' | 'reviewed';
  scope: string | null;
  reviewedBy: string | null;
  reviewedAt: number | null;
  originProjectId: string | null;
  originFormCode: string | null;
  originQuestionLabel: string | null;
  // Provenance back to the source CRF (seeds carry packet/form/page).
  sourcePacket: string | null;
  formName: string | null;
  pageHint: number | null;
  studyTermFlags: string[];
  usageCount: number;
  createdAt: number | null;
  updatedAt: number | null;
}

interface ListCursor {
  usage: number;
  id: string;
}

interface ListResponse {
  entries: LibraryEntry[];
  nextCursor: ListCursor | null;
}

interface DraftVariant {
  entryId: string;
  client: string | null;
  instruction: string;
  studyTermFlags: string[];
}

interface DraftResponse {
  draft: string;
  variants: DraftVariant[];
}

interface ApproveGeneralizedResponse {
  globalEntryId: string;
  replacedCount: number;
}

/** Cluster card opened after a successful generalization draft. */
interface ClusterCard {
  groupKey: string;
  entryIds: string[];
  draft: string;
  variants: DraftVariant[];
  /** Union of variant studyTermFlags. */
  flags: string[];
  variable: string | null;
  normalizedLabel: string;
  questionType: string;
}

/** Per-request page size for the behind-the-scenes full load. */
const PAGE_SIZE = 25;
/**
 * Safety cap on the full load. If a status has more entries than this we
 * stop fetching and surface a note prompting the user to narrow filters.
 */
/** The draft/approve cluster callables accept 2–20 entry ids. */
const MAX_CLUSTER_SIZE = 20;

/** Sortable columns. */
type SortKey =
  | 'question'
  | 'variable'
  | 'study'
  | 'form'
  | 'client'
  | 'uses'
  | 'date'
  | 'status';
type SortDir = 'asc' | 'desc';

/**
 * Cap on rendered row-blocks. A status like "Needs review" can carry
 * ~1,400 entries; rendering them all as expandable rows hangs the browser.
 * Load/facet/sort logic still runs over the full set — only the DOM render
 * is capped. Narrow filters (study/form/client/reused) to reach the rest.
 */

// ── Helpers ────────────────────────────────────────────────────────────

const formatMillis = (ms: number | null): string =>
  ms
    ? new Date(ms).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'N/A';

/** Compact date for the "Date added" column, e.g. "Jun 12, 2026". */
const formatDate = (ms: number | null): string =>
  ms
    ? new Date(ms).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

const groupKeyOf = (e: LibraryEntry): string =>
  `${e.normalizedLabel}|${e.questionType}`;

/**
 * Short study/packet token for the Study column — derived without expanding
 * a row. From sourcePacket we take the leading code: everything before the
 * first " CRF"/"_CRF" (case-insensitive) or the first space/underscore,
 * with underscores turned into hyphens. Examples:
 *   "ATRA_CRFs_20241106.pdf"        -> "STUDY_C"
 *   "DBS_STUDY_B CRFs v3.0.20251219.pdf" -> "DBS-STUDY_B"
 *   "STUDY_A CRF Packet D2.0.20260414.pdf" -> "STUDY_A"
 * Falls back to the raw packet if the derivation yields empty, then to the
 * harvested study id (truncated), then "—".
 */
const studyLabel = (e: LibraryEntry): string => {
  if (e.sourcePacket) {
    const packet = e.sourcePacket;
    // Prefer the chunk before " CRF" / "_CRF" (case-insensitive).
    const crfMatch = packet.match(/^(.*?)[ _]CRF/i);
    let token = crfMatch ? crfMatch[1] : '';
    if (!token) {
      // No "CRF" marker — take up to the first space or underscore.
      token = packet.split(/[ _]/)[0] ?? '';
    }
    token = token.trim().replace(/_/g, '-');
    return token || packet;
  }
  if (e.originProjectId && e.originProjectId !== 'seed') {
    return e.originProjectId.length > 12
      ? e.originProjectId.slice(0, 12)
      : e.originProjectId;
  }
  return '—';
};

function StatusBadge({ entry }: { entry: LibraryEntry }) {
  const reviewed = entry.status === 'reviewed';
  return (
    <span className="inline-flex max-w-full flex-col items-start gap-1.5">
      <span
        className={
          reviewed
            ? 'inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium'
            : 'inline-flex items-center px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium'
        }
      >
        {reviewed ? 'Approved' : 'Needs review'}
      </span>
      {entry.source === 'fallback' && (
        <span className="inline-flex max-w-full items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
          fallback
        </span>
      )}
    </span>
  );
}

function StudyTermChip({ terms }: { terms: string[] }) {
  if (terms.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
      Contains study-specific terms: {terms.join(', ')}
    </span>
  );
}

/** Clickable, sort-aware table header cell. */
function SortHeader({
  label,
  colKey,
  width,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string;
  colKey: SortKey;
  width: string;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const active = sortKey === colKey;
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase ${width}`}>
      <button
        type="button"
        onClick={() => onSort(colKey)}
        className={`inline-flex items-center gap-1 uppercase hover:text-gray-700 ${
          active ? 'text-gray-700' : ''
        }`}
      >
        {label}
        {active &&
          (sortDir === 'asc' ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          ))}
      </button>
    </th>
  );
}

// ── Page ───────────────────────────────────────────────────────────────

export default function InstructionLibrary() {
  const { showToast } = useToast();

  // Review status — drives a fresh full load. Default "Approved" (reviewed)
  // so the seeded standard library is visible on first open.
  const [statusFilter, setStatusFilter] = useState<'unreviewed' | 'reviewed'>(
    'reviewed',
  );

  // Client-side facets over the loaded set.
  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedForm, setSelectedForm] = useState('');
  // Client text box value vs. applied filter (applied on Enter/blur).
  const [clientInput, setClientInput] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  // Default OFF: we now load everything, so the user opts into the reused
  // worklist view rather than it hiding the (0-use) seeded library.
  const [reusedOnly, setReusedOnly] = useState(false);

  // Sort (default usage-desc, preserving prior behavior).
  const [sortKey, setSortKey] = useState<SortKey>('uses');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Full loaded set + load state
  const [allEntries, setAllEntries] = useState<LibraryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);
  const [nextCursor, setNextCursor] = useState<ListCursor | null>(null);
  const [pageCursors, setPageCursors] = useState<Array<ListCursor | null>>([null]);

  // Per-entry review card
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  // Cluster review
  const [draftingKey, setDraftingKey] = useState<string | null>(null);
  const [clusterCard, setClusterCard] = useState<ClusterCard | null>(null);
  const [approvingCluster, setApprovingCluster] = useState(false);

  // Discard list responses that arrive after a newer load started
  // (e.g. rapid status changes).
  const requestIdRef = useRef(0);

  // Fetch one server page at a time. Cursors for visited pages are cached so
  // Previous is instant to address and Next never preloads unseen rows.
  const loadPage = useCallback(async (cursor: ListCursor | null, page: number) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    // Fresh load — clear prior error. Safe under the race-guard: requestId
    // was just set to the latest id above.
    setLoadError(null);
    setCapped(false);
    try {
      const list = httpsCallable(
        functions,
        CLOUD_FUNCTIONS.LIST_INSTRUCTION_LIBRARY,
      );
      const result = await list({
          status: statusFilter,
          reusedOnly,
          client: clientFilter.trim() || undefined,
          pageSize: PAGE_SIZE,
          cursor,
        });
      if (requestId !== requestIdRef.current) return;
      const data = result.data as ListResponse;
      setAllEntries(data.entries);
      setNextCursor(data.nextCursor);
      setCurrentPage(page);
      setPageCursors((existing) => {
        const updated = existing.slice(0, page);
        if (data.nextCursor) updated[page] = data.nextCursor;
        return updated;
      });
      setCapped(false);
      // Fresh result set — anything expanded may no longer exist.
      setExpandedId(null);
      setClusterCard(null);
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return;
      const error = err as { message?: string };
      const message = error.message || 'Failed to load instruction library';
      setLoadError(message);
      showToast(message, 'error');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [statusFilter, reusedOnly, clientFilter, showToast]);

  const loadAll = useCallback(async () => {
    setPageCursors([null]);
    await loadPage(null, 1);
  }, [loadPage]);

  // Re-load on mount and whenever the review status changes.
  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // ── Client-side facets + sort ────────────────────────────────────────
  const visibleEntries = useMemo(() => {
    const clientNeedle = clientFilter.trim().toLowerCase();
    const filtered = allEntries.filter((e) => {
      if (selectedStudy && studyLabel(e) !== selectedStudy) return false;
      if (selectedForm && e.originFormCode !== selectedForm) return false;
      if (reusedOnly && !(e.usageCount > 0)) return false;
      if (clientNeedle) {
        const c = (e.client ?? '').toLowerCase();
        if (!c.includes(clientNeedle)) return false;
      }
      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    // localeCompare with nulls/empties last (independent of direction).
    const cmpStr = (a: string | null, b: string | null): number => {
      const av = a ?? '';
      const bv = b ?? '';
      if (!av && !bv) return 0;
      if (!av) return 1 * dir; // empties always sink to the bottom
      if (!bv) return -1 * dir;
      return av.localeCompare(bv) * dir;
    };
    const cmpNum = (a: number | null, b: number | null): number =>
      ((a ?? 0) - (b ?? 0)) * dir;

    const sorted = [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'question':
          return cmpStr(
            a.originQuestionLabel ?? a.normalizedLabel,
            b.originQuestionLabel ?? b.normalizedLabel,
          );
        case 'variable':
          return cmpStr(a.variable, b.variable);
        case 'study':
          return cmpStr(studyLabel(a), studyLabel(b));
        case 'form':
          return cmpStr(a.originFormCode, b.originFormCode);
        case 'client':
          return cmpStr(a.client, b.client);
        case 'uses':
          return cmpNum(a.usageCount, b.usageCount);
        case 'date':
          return cmpNum(a.createdAt, b.createdAt);
        case 'status':
          return cmpStr(a.status, b.status);
        default:
          return 0;
      }
    });
    return sorted;
  }, [
    allEntries,
    selectedStudy,
    selectedForm,
    reusedOnly,
    clientFilter,
    sortKey,
    sortDir,
  ]);

  // Distinct study tokens present in the loaded set, alphabetical
  // (excluding the "—" placeholder so it doesn't read as a selectable study).
  const studyOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of allEntries) {
      const label = studyLabel(e);
      if (label && label !== '—') set.add(label);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allEntries]);

  // Distinct form codes present in the loaded set, alphabetical.
  const formOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of allEntries) {
      if (e.originFormCode) set.add(e.originFormCode);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allEntries]);

  const toggleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        // Numeric/date columns read best high-first; text low-first.
        setSortDir(key === 'uses' || key === 'date' ? 'desc' : 'asc');
      }
    },
    [sortKey],
  );

  const safePage = currentPage;
  const pagedEntries = visibleEntries;

  useEffect(() => {
    if (currentPage !== 1) void loadAll();
  }, [selectedStudy, selectedForm, sortKey, sortDir]);

  // ── Grouping for cluster review ──────────────────────────────────────
  // Group the entries on the current page by normalizedLabel|questionType,
  // preserving the filtered/sorted order: each group renders at the position
  // of its first member, with later members pulled up under the group header.
  const renderBlocks = useMemo(() => {
    const byKey = new Map<string, LibraryEntry[]>();
    for (const e of pagedEntries) {
      const key = groupKeyOf(e);
      const members = byKey.get(key);
      if (members) {
        members.push(e);
      } else {
        byKey.set(key, [e]);
      }
    }
    const seen = new Set<string>();
    const blocks: Array<{ key: string; members: LibraryEntry[] }> = [];
    for (const e of pagedEntries) {
      const key = groupKeyOf(e);
      if (seen.has(key)) continue;
      seen.add(key);
      blocks.push({ key, members: byKey.get(key)! });
    }
    return blocks;
  }, [pagedEntries]);

  // ── Actions ──────────────────────────────────────────────────────────

  const toggleExpand = (entry: LibraryEntry) => {
    if (expandedId === entry.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(entry.id);
    setEditText(entry.instruction);
  };

  const handleReview = async (
    entry: LibraryEntry,
    action: 'approve' | 'reject',
    scope?: 'client' | 'global',
  ) => {
    if (action === 'reject') {
      if (
        !window.confirm(
          'Remove this instruction? Future generations will write a fresh one.',
        )
      ) {
        return;
      }
    }
    setReviewBusy(true);
    try {
      const review = httpsCallable(functions, CLOUD_FUNCTIONS.REVIEW_INSTRUCTION);
      const payload: Record<string, unknown> = {
        entryId: entry.entryId,
        action,
      };
      if (action === 'approve') {
        payload.scope = scope;
        payload.instruction = editText;
      }
      await review(payload);
      showToast(
        action === 'reject'
          ? 'Instruction removed'
          : scope === 'global'
            ? 'Instruction approved for everyone'
            : 'Instruction approved for this client',
        'success',
      );
      setExpandedId(null);
      await loadAll();
    } catch (err: unknown) {
      // failed-precondition messages (e.g. remaining study-specific terms
      // on a global approve) are admin guidance — surface them verbatim.
      const error = err as { message?: string };
      showToast(error.message || 'Failed to update instruction', 'error');
    } finally {
      setReviewBusy(false);
    }
  };

  const handleDraftGeneralized = async (
    key: string,
    members: LibraryEntry[],
  ) => {
    const clusterMembers = members.slice(0, MAX_CLUSTER_SIZE);
    const entryIds = clusterMembers.map((m) => m.entryId);
    setDraftingKey(key);
    try {
      const draft = httpsCallable(
        functions,
        CLOUD_FUNCTIONS.DRAFT_GENERALIZED_INSTRUCTION,
        { timeout: CALLABLE_TIMEOUTS.SHORT },
      );
      const result = await draft({ entryIds });
      const data = result.data as DraftResponse;
      const flags = Array.from(
        new Set(data.variants.flatMap((v) => v.studyTermFlags ?? [])),
      );
      setClusterCard({
        groupKey: key,
        entryIds,
        draft: data.draft,
        variants: data.variants,
        flags,
        variable: clusterMembers[0].variable,
        normalizedLabel: clusterMembers[0].normalizedLabel,
        questionType: clusterMembers[0].questionType,
      });
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Failed to draft a generalized instruction', 'error');
    } finally {
      setDraftingKey(null);
    }
  };

  const handleApproveGeneralized = async () => {
    if (!clusterCard) return;
    setApprovingCluster(true);
    try {
      const approve = httpsCallable(
        functions,
        CLOUD_FUNCTIONS.APPROVE_GENERALIZED_INSTRUCTION,
      );
      const result = await approve({
        entryIds: clusterCard.entryIds,
        instruction: clusterCard.draft,
        variable: clusterCard.variable,
        normalizedLabel: clusterCard.normalizedLabel,
        questionType: clusterCard.questionType,
      });
      const data = result.data as ApproveGeneralizedResponse;
      showToast(
        `Generalized instruction approved — replaced ${data.replacedCount} ${
          data.replacedCount === 1 ? 'entry' : 'entries'
        }`,
        'success',
      );
      setClusterCard(null);
      await loadAll();
    } catch (err: unknown) {
      // failed-precondition messages (existing global entry outside the
      // cluster, remaining study-specific terms) are admin guidance —
      // surface them verbatim.
      const error = err as { message?: string };
      showToast(error.message || 'Failed to approve generalized instruction', 'error');
    } finally {
      setApprovingCluster(false);
    }
  };

  const anyActionBusy = reviewBusy || approvingCluster || draftingKey !== null;

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#F7F9FB]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <header className="border-b border-[#DCE2E5] bg-white/90">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-7 lg:px-10">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-[var(--brand-primary)]" />
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Content governance</p>
                <h1 className="text-3xl font-normal tracking-[-0.035em] text-[#001820]">Instruction Library</h1>
                <p className="text-sm text-gray-500">
                  Review reusable CRF completion instructions harvested from generated guides.
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 py-8 lg:px-10">
        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-4 rounded-2xl border border-[#DCE2E5] bg-white p-5 shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
          {/* Review status — segmented toggle. Changing it re-loads. */}
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setStatusFilter('unreviewed')}
              className={`px-3 py-1.5 text-sm font-medium ${
                statusFilter === 'unreviewed'
                  ? 'bg-[#B46B08] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Needs review
            </button>
            <button
              onClick={() => setStatusFilter('reviewed')}
              className={`px-3 py-1.5 text-sm font-medium border-l border-gray-300 ${
                statusFilter === 'reviewed'
                  ? 'bg-[var(--brand-primary)] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Approved
            </button>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Study
            <select
              value={selectedStudy}
              onChange={(e) => setSelectedStudy(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All studies</option>
              {studyOptions.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Form
            <select
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All forms</option>
              {formOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Client
            <input
              type="text"
              value={clientInput}
              onChange={(e) => setClientInput(e.target.value)}
              onBlur={() => setClientFilter(clientInput)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="Filter by client"
              className="border rounded px-2 py-1.5 text-sm w-48"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={reusedOnly}
              onChange={(e) => setReusedOnly(e.target.checked)}
              className="rounded"
            />
            Show only reused
          </label>
          <button
            onClick={() => void loadAll()}
            disabled={loading}
            className="ml-auto flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {capped && (
          <div className="mb-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            Results are limited — narrow filters to see more.
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-400" />
            Loading entries...
          </div>
        ) : loadError && allEntries.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-400" />
            <p className="text-gray-700">
              Couldn't load the library — {loadError}
            </p>
            <button
              onClick={() => void loadAll()}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : visibleEntries.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            No entries match these filters.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-2xl border border-[#DCE2E5] bg-white shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <SortHeader
                      label="Question"
                      colKey="question"
                      width="w-[15%]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortHeader
                      label="Variable"
                      colKey="variable"
                      width="w-[8%]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[22%]">Instruction</th>
                    <SortHeader
                      label="Study"
                      colKey="study"
                      width="w-[9%]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortHeader
                      label="Client"
                      colKey="client"
                      width="w-[9%]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortHeader
                      label="Form"
                      colKey="form"
                      width="w-[8%]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortHeader
                      label="Uses"
                      colKey="uses"
                      width="w-[6%]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortHeader
                      label="Date added"
                      colKey="date"
                      width="w-[10%]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortHeader
                      label="Status"
                      colKey="status"
                      width="w-[13%]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {renderBlocks.map(({ key, members }) => {
                    const isCluster = members.length >= 2;
                    return (
                      <BlockRows
                        key={key}
                        groupKey={key}
                        members={members}
                        isCluster={isCluster}
                        expandedId={expandedId}
                        editText={editText}
                        onEditTextChange={setEditText}
                        onToggleExpand={toggleExpand}
                        onReview={handleReview}
                        reviewBusy={reviewBusy}
                        anyActionBusy={anyActionBusy}
                        draftingKey={draftingKey}
                        onDraft={handleDraftGeneralized}
                        clusterCard={clusterCard}
                        onClusterDraftChange={(text) =>
                          setClusterCard((prev) =>
                            prev ? { ...prev, draft: text } : prev,
                          )
                        }
                        onApproveCluster={handleApproveGeneralized}
                        onCancelCluster={() => setClusterCard(null)}
                        approvingCluster={approvingCluster}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm text-[#5F696C]">
                Page {safePage} · {visibleEntries.length.toLocaleString()} entr{visibleEntries.length === 1 ? 'y' : 'ies'} loaded on demand
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void loadPage(pageCursors[safePage - 2] ?? null, safePage - 1)}
                  disabled={safePage === 1}
                  className="inline-flex items-center gap-1 rounded-full border border-[#CAD2D5] bg-white px-4 py-2 text-sm font-semibold text-[#35565C] transition hover:border-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                <span className="min-w-20 text-center text-sm font-semibold text-[#001820]">Page {safePage}</span>
                <button
                  type="button"
                  onClick={() => nextCursor && void loadPage(nextCursor, safePage + 1)}
                  disabled={!nextCursor}
                  className="inline-flex items-center gap-1 rounded-full border border-[#CAD2D5] bg-white px-4 py-2 text-sm font-semibold text-[#35565C] transition hover:border-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// ── Row blocks ─────────────────────────────────────────────────────────

interface BlockRowsProps {
  groupKey: string;
  members: LibraryEntry[];
  isCluster: boolean;
  expandedId: string | null;
  editText: string;
  onEditTextChange: (text: string) => void;
  onToggleExpand: (entry: LibraryEntry) => void;
  onReview: (
    entry: LibraryEntry,
    action: 'approve' | 'reject',
    scope?: 'client' | 'global',
  ) => void;
  reviewBusy: boolean;
  anyActionBusy: boolean;
  draftingKey: string | null;
  onDraft: (key: string, members: LibraryEntry[]) => void;
  clusterCard: ClusterCard | null;
  onClusterDraftChange: (text: string) => void;
  onApproveCluster: () => void;
  onCancelCluster: () => void;
  approvingCluster: boolean;
}

function BlockRows({
  groupKey,
  members,
  isCluster,
  expandedId,
  editText,
  onEditTextChange,
  onToggleExpand,
  onReview,
  reviewBusy,
  anyActionBusy,
  draftingKey,
  onDraft,
  clusterCard,
  onClusterDraftChange,
  onApproveCluster,
  onCancelCluster,
  approvingCluster,
}: BlockRowsProps) {
  const cardOpenHere = clusterCard !== null && clusterCard.groupKey === groupKey;
  return (
    <>
      {isCluster && (
        <tr className="bg-blue-50/60">
          <td colSpan={9} className="px-4 py-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-blue-900">
                {members.length} variants of the same question
              </span>
              <button
                onClick={() => onDraft(groupKey, members)}
                disabled={anyActionBusy}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {draftingKey === groupKey && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                {draftingKey === groupKey ? 'Drafting...' : 'Draft generalized'}
              </button>
            </div>
          </td>
        </tr>
      )}

      {/* Cluster review card (under the group header) */}
      {cardOpenHere && clusterCard && (
        <tr className="bg-blue-50/30">
          <td colSpan={9} className="px-4 py-4">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Generalized instruction
                </label>
                <textarea
                  rows={4}
                  value={clusterCard.draft}
                  onChange={(e) => onClusterDraftChange(e.target.value)}
                  className="w-full border rounded-lg p-3 text-sm text-gray-900"
                />
              </div>
              {clusterCard.flags.length > 0 && (
                <div>
                  <StudyTermChip terms={clusterCard.flags} />
                </div>
              )}
              <div className="border rounded-lg divide-y bg-white">
                {clusterCard.variants.map((v) => (
                  <div key={v.entryId} className="px-3 py-2 flex gap-3 text-sm">
                    <span className="flex-shrink-0 w-32 font-medium text-gray-700 truncate">
                      {v.client ?? '—'}
                    </span>
                    <span className="text-gray-600">{v.instruction}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onApproveCluster}
                  disabled={anyActionBusy}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {approvingCluster && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  Approve for everyone (replaces {clusterCard.entryIds.length})
                </button>
                <button
                  onClick={onCancelCluster}
                  disabled={approvingCluster}
                  className="px-3 py-1.5 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}

      {members.map((entry) => (
        <EntryRows
          key={entry.id}
          entry={entry}
          indented={isCluster}
          expanded={expandedId === entry.id}
          editText={editText}
          onEditTextChange={onEditTextChange}
          onToggleExpand={onToggleExpand}
          onReview={onReview}
          reviewBusy={reviewBusy}
          anyActionBusy={anyActionBusy}
        />
      ))}
    </>
  );
}

interface EntryRowsProps {
  entry: LibraryEntry;
  indented: boolean;
  expanded: boolean;
  editText: string;
  onEditTextChange: (text: string) => void;
  onToggleExpand: (entry: LibraryEntry) => void;
  onReview: (
    entry: LibraryEntry,
    action: 'approve' | 'reject',
    scope?: 'client' | 'global',
  ) => void;
  reviewBusy: boolean;
  anyActionBusy: boolean;
}

function EntryRows({
  entry,
  indented,
  expanded,
  editText,
  onEditTextChange,
  onToggleExpand,
  onReview,
  reviewBusy,
  anyActionBusy,
}: EntryRowsProps) {
  return (
    <>
      <tr
        onClick={() => onToggleExpand(entry)}
        className={`cursor-pointer hover:bg-gray-50 ${expanded ? 'bg-gray-50' : ''}`}
      >
        <td className={`px-4 py-3 text-sm text-gray-900 ${indented ? 'pl-8' : ''}`}>
          <span className="line-clamp-2">
            {entry.originQuestionLabel ?? entry.normalizedLabel}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 font-mono truncate">
          {entry.variable ?? '—'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          <span className="line-clamp-2">{entry.instruction}</span>
        </td>
        <td className="px-4 py-3 text-xs text-gray-600 truncate" title={studyLabel(entry)}>
          {studyLabel(entry)}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 truncate">
          {entry.client ?? '—'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 truncate">
          {entry.originFormCode ?? '—'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">{entry.usageCount}</td>
        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
          {formatDate(entry.createdAt)}
        </td>
        <td className="px-4 py-3">
          <StatusBadge entry={entry} />
        </td>
      </tr>

      {/* Inline review card */}
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={9} className="px-4 py-4">
            <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
              {entry.studyTermFlags.length > 0 && (
                <div>
                  <StudyTermChip terms={entry.studyTermFlags} />
                </div>
              )}
              <textarea
                rows={4}
                value={editText}
                onChange={(e) => onEditTextChange(e.target.value)}
                className="w-full border rounded-lg p-3 text-sm text-gray-900"
              />
              {/* Source provenance — where to find the originating CRF. */}
              <div className="text-xs text-gray-600">
                <span className="font-medium text-gray-700">Source:</span>{' '}
                {entry.sourcePacket ? (
                  <>
                    {entry.sourcePacket}
                    {' '}&middot;{' '}
                    {entry.formName
                      ? `${entry.formName} (${entry.originFormCode ?? '—'})`
                      : (entry.originFormCode ?? '—')}
                    {typeof entry.pageHint === 'number' && (
                      <> &middot; p.{entry.pageHint}</>
                    )}
                  </>
                ) : entry.originProjectId && entry.originProjectId !== 'seed' ? (
                  <>
                    Harvested from study{' '}
                    <span className="font-mono">{entry.originProjectId}</span>
                    {' '}&middot; form{' '}
                    {entry.formName
                      ? `${entry.formName} (${entry.originFormCode ?? '—'})`
                      : (entry.originFormCode ?? '—')}
                  </>
                ) : (
                  <>form {entry.originFormCode ?? '—'}</>
                )}
              </div>
              {entry.reviewedBy && (
                <div className="text-xs text-gray-500">
                  Reviewed by {entry.reviewedBy}
                  {entry.reviewedAt !== null && (
                    <> on {formatMillis(entry.reviewedAt)}</>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onReview(entry, 'approve', 'client')}
                  disabled={anyActionBusy}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Approve for this client
                </button>
                <button
                  onClick={() => onReview(entry, 'approve', 'global')}
                  disabled={anyActionBusy}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Approve for everyone
                </button>
                <button
                  onClick={() => onReview(entry, 'reject')}
                  disabled={anyActionBusy}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  Reject
                </button>
                {reviewBusy && (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
